/* eslint-disable no-alert -- alert for api error */
import React, { useEffect } from 'react';
import { Container, Row, Col, Loader } from 'shared-portal-component';
import { Outlet } from '@tanstack/react-router';
import { useSelector } from 'react-redux';

import Header from '@/components/molecules/Header/Header';
import Sidebar from '@/components/molecules/Sidebar/Sidebar';
import '@/layout/AdminDashboardLayout/AdminDashboardLayout.scss';
import { useAdminProfile } from '@/hooks/useAdminProfile';
import { selectIsSubmitting } from '@/store/selectors/auth';
import { selectErrorMessage } from '@/store/selectors/ui';
import { setErrorMessage } from '@/store/slices/ui/UISlice';
import { type RootState, useAppDispatch } from '@/store';
import AdminModalContainer from '@/components/common/AdminModalContainer/AdminModalContainer';
import ConfirmModalContainer from '@/components/common/Confirm/ConfirmModalContainer';

const AdminDashboardLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { profile } = useAdminProfile();
  const authSubmitting = useSelector(selectIsSubmitting);
  const adminFormSubmitting = useSelector((s: RootState) => s.administrators.isSubmitting);
  const isSubmitting = authSubmitting || adminFormSubmitting;

  const errorMessage = useSelector(selectErrorMessage);

  const STR_MIN = 0;
  const STR_MAX = 2;

  const customerName = profile?.name ?? '—';
  const companyName = profile?.companyName ?? '—';
  const customerInitials = customerName
    .split(' ')
    .map((word) => word[STR_MIN])
    .join('')
    .slice(STR_MIN, STR_MAX)
    .toUpperCase();

  const logoSrc = '/portalSixLogo.svg';

  useEffect(() => {
    if (errorMessage !== null) {
      window.alert(`Something went wrong: ${errorMessage}`);
      dispatch(setErrorMessage(null));
    }
  }, [errorMessage, dispatch]);

  return (
    <Container fluid className="admin-layout">
      {isSubmitting && (
        <div className="global-loader-overlay" role="alert" aria-busy="true" aria-live="polite">
          <Loader />
        </div>
      )}

      <Row spacing="none">
        <Col size={12}>
          <Header
            logo={{ imageSrc: logoSrc, alt: 'PortalSIX Logo' }}
            customerName={customerName}
            customerInitials={customerInitials}
            companyName={companyName}
          />
        </Col>
      </Row>

      <Row spacing="none" className="admin-body">
        <Col size="content">
          <Sidebar />
        </Col>

        <Col className="admin-content">
          <Row spacing="md" className="admin-content-grid">
            <Col size={12}>
              {/* Globally-mounted modal & confirm so they work from ANY page */}
              <AdminModalContainer />
              <ConfirmModalContainer />
              <Outlet />
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboardLayout;
